/**
 * Panels 3 and 7 — `Teams Coverage` (§B7) and its AI counterpart (§B9).
 *
 * An umpire covers **both** teams of any match they hold any of the four slots in, so each
 * appointment adds two counts. The foot says so, for the reason the technical page's does: a
 * reader who has to work that out has already half-decided the table is wrong.
 */

import { COMPETITION_TABS, Tabs } from "../appointments/Tabs.js";
import { competitionLabel, type CompetitionFilter } from "../appointments/derive.js";
import { cellClass } from "../appointments/useCellFlash.js";
import { heatClass, type UmpireDataset, type UmpireTeamsTable } from "./derive.js";

export interface UmpireTeamsPanelProps {
  dataset: UmpireDataset;
  table: UmpireTeamsTable;
  competition: CompetitionFilter;
  onCompetition: (value: CompetitionFilter) => void;
  flashing: ReadonlySet<string>;
}

export function UmpireTeamsPanel({
  dataset,
  table,
  competition,
  onCompetition,
  flashing,
}: UmpireTeamsPanelProps): React.JSX.Element {
  const tms = dataset === "tmsActual";
  const prefix = tms ? "a" : "r";
  const id = tms ? "umpTcTitle" : "aiTcTitle";
  const { teams, rows } = table;

  return (
    <section
      className={`panel ${tms ? "panel--d3" : "panel--ai"}`}
      aria-labelledby={id}
      data-testid={tms ? "tms-coverage" : "ai-coverage"}
    >
      <div className="panel__head">
        <h3 className="panel__title" id={id}>
          {tms ? "Teams Coverage" : "AI Drafted Teams Coverage"}
        </h3>
        <span className="panel__note">
          {tms ? "Team-Exposure Fairness Check" : "Team-Exposure Yardstick"}
        </span>
        <span className="spacer" />
        <span className="filtlab">Competition</span>
        <Tabs
          label={
            tms ? "Teams coverage competition filter" : "AI teams coverage competition filter"
          }
          options={COMPETITION_TABS}
          value={competition}
          onChange={onCompetition}
          tint="competition"
        />
      </div>

      <div className="matwrap tfwrap">
        <table
          className="mattable tftable"
          aria-label={
            tms
              ? "Teams coverage — matches involving each team per umpire"
              : "AI drafted teams coverage"
          }
        >
          <thead>
            <tr className="matgrp">
              <th scope="col">Umpires</th>
              <th scope="col" colSpan={teams.length + 1} className="g-right">
                Matches Involving Each Team
              </th>
            </tr>
            <tr>
              <th scope="col" className="c-name">
                Umpire
              </th>
              {teams.map((team, index) => (
                <th key={team} scope="col" className={index === 0 ? "first-right" : undefined}>
                  {team}
                </th>
              ))}
              <th scope="col" className="c-tot">
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const personId = row.umpire.tmsPersonId;
              return (
                <tr key={personId} data-testid={`${prefix}-coverage-${personId}`}>
                  <td
                    className={`c-name${row.umpire.gender === null ? "" : ` g-${row.umpire.gender}`}`}
                  >
                    <span className="rolechip">
                      {row.umpire.panel === "AHF" ? "AHF" : "NEU"}
                    </span>
                    {row.umpire.surname}
                    <span className="nat num">{row.umpire.country}</span>
                  </td>
                  {teams.map((team, index) => {
                    const value = row.per[team] ?? 0;
                    return (
                      <td
                        key={team}
                        className={cellClass(
                          `${heatClass(value)}${index === 0 ? " first-right" : ""}`,
                          `${prefix}f:${personId}:${team}`,
                          flashing,
                        )}
                      >
                        {value}
                      </td>
                    );
                  })}
                  <td className={cellClass("c-tot", `${prefix}f:${personId}:tot`, flashing)}>
                    {row.total}
                  </td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr>
              <td className="c-name">Total</td>
              {teams.map((team, index) => (
                <td key={team} className={index === 0 ? "first-right" : undefined}>
                  {table.columnTotals[team] ?? 0}
                </td>
              ))}
              <td>{table.grandTotal}</td>
            </tr>
          </tfoot>
        </table>

        {teams.length === 0 ? (
          <div className="emptystate">
            <b>No teams in this view.</b>
            Coverage appears as soon as this half carries a match in this competition.
          </div>
        ) : null}
      </div>

      <div className="panel__foot">
        <div className="matlegend" aria-label="Team exposure heat key">
          <span>Team Exposure</span>
          <span className="hsw">
            <i className="k0" aria-hidden="true" />0
          </span>
          <span className="hsw">
            <i className="k1" aria-hidden="true" />1
          </span>
          <span className="hsw">
            <i className="k2" aria-hidden="true" />2
          </span>
          <span className="hsw">
            <i className="k3" aria-hidden="true" />
            3+
          </span>
        </div>
        {tms ? (
          <>
            <b>Reading the table —</b> an umpire covers <b>both</b> teams of any match they are
            appointed to, in any of the four roles, so each appointment adds two counts. Darker
            cells mean more exposure to that team.
          </>
        ) : (
          <>
            <b>Compare with the coverage above —</b> a flatter spread here means the engine
            would rotate teams more evenly than the Umpires Managers&rsquo; appointments above do.
          </>
        )}{" "}
        <span data-testid={`${prefix}-coverage-scope`}>
          Currently reflecting <b>{table.fixtures}</b>{" "}
          {competition === "all" ? "" : `${competitionLabel(competition)} `}
          {table.fixtures === 1 ? "match" : "matches"} across <b>{teams.length}</b>{" "}
          {teams.length === 1 ? "team" : "teams"}.
        </span>
      </div>
    </section>
  );
}
